// The ServiceTitan move (gap #16; the st-migration prototype): the shapes of the migration.* actions
// and their words, kept apart from the screens so they are tested. Who sees which control mirrors
// packages/auth's catalogue (roles.ts), one list per permission. It runs against the fake ServiceTitan
// until the company's owner has given the written OK for a real pull.

import { holdsAny } from '../shared/may.ts';

/** migration.view: the owner, the manager, the dispatcher and the bookkeeper (who see the place). */
export const VIEW_PERMS = ['migration.view'];
/** migration.operate (run, map, link, resolve, cutover steps): the owner and the manager. */
export const OPERATE_PERMS = ['migration.operate'];
/** migration.approve and migration.signOff (owner-only in the catalogue): authorise, approve, sign off. */
export const OWNER_PERMS = ['migration.approve'];

/** What every part of the place is given (kept here so the parts and the screen don't import each other). */
export interface PartProps {
  /** The permissions the person holds (BL-137: permissions, not role names, decide what opens). */
  held: readonly string[];
  toast: (m: string) => void;
  timeZone: string;
}

/** Whether the person holds any of these permissions. */
export const holds = holdsAny;

export interface Run {
  state: 'off' | 'running' | 'paused';
  phase: string;
  stTenant: string | null;
  years: number[];
  loadRequested: boolean;
  lastTickAt: string | null;
  lastStep: string | null;
  lastError: string | null;
  /** While mirroring: the next pass, and whether ServiceTitan asked us to wait for it. */
  mirror?: { nextAt: string | null; waitingOnServiceTitan: boolean } | null;
  /** The years the load asked for (null: everything), and the years loaded so far. */
  loadYears?: number[] | null;
  loadedYears?: number[];
  /** What the run reads: newest years, a test window, or (null) everything. */
  scope?: { years: number } | { window: { from: string; to: string } } | null;
  /** A switch to live stopped half way (st-mirror §4 12e); null otherwise. */
  goingLive?: { since: string; minutes: number } | null;
}

/**
 * The year "Load what's staged" loads when nobody picked one (#80: Amir's press with no years took every
 * staged year, ahead of his approvals): the newest of the run's years not loaded yet, else the newest.
 * Null when there's no year to name: none read yet, a test window (it loads whole), or the mirror.
 */
export function yearToLoad(run: Run | null): number | null {
  if (!run || run.phase === 'mirror' || (run.scope && 'window' in run.scope) || !run.years.length)
    return null;
  const newest = [...run.years].sort((a, b) => b - a);
  const loaded = new Set(run.loadedYears ?? []);
  return newest.find((y) => !loaded.has(y)) ?? newest[0]!;
}

/**
 * What the Load button sends (migration.requestLoad): the year picked, else yearToLoad's, always named;
 * nothing only where a load takes none (the mirror's pass, a test window).
 */
export function loadInput(run: Run | null, picked: number | null): { years?: number[] } {
  const year = picked ?? yearToLoad(run);
  return year === null ? {} : { years: [year] };
}

/**
 * What a load asked for does next, in words (st-mirror spec §1): the staged data is loaded; in the mirror
 * a pass runs within the minute, or when ServiceTitan's wait is over (the owner's load never overrides it).
 */
export function loadAskedWords(run: Run, when: (iso: string) => string): string {
  if (run.phase !== 'mirror')
    return run.loadYears?.length
      ? `Asked for: the worker loads ${yearsText(run.loadYears)} next.`
      : 'Asked for: the worker loads what’s staged next.';
  const m = run.mirror;
  if (m?.waitingOnServiceTitan && m.nextAt)
    return `ServiceTitan asked us to wait; the load runs at ${when(m.nextAt)}.`;
  return 'Asked for: the mirror loads ServiceTitan’s changes within the minute.';
}

export interface Status {
  units: { pending: number; running: number; done: number; parked: number };
  rows: number;
  /** The records staged in each year (the sample left out): what the office's line counts a year by. */
  staged?: { year: number; rows: number }[];
  files: { downloaded: number; skippedRetention: number; failed: number; listed: number; deferred?: number };
  /** Jobs' photo lists (M9): waiting, read, and given up on with the latest refusal. */
  photos?: { pending: number; listed: number; failed: number; lastError: string | null };
  linksWaiting: number;
  reviewOpen: number;
  /** Of reviewOpen: ServiceTitan's records (each year's sign-off counts its own) and Brook's import's. */
  reviewOpenServiceTitan?: number;
  reviewOpenBrook?: number;
  /** Each year's sign-off, newest first: signed, or what its latest reconciliation has red. */
  signOff?: SignOffYear[];
  /** Of the groups with values read, those a person approved. */
  groupsApproved: number;
  /** The groups with values read. */
  groups: number;
  /** Groups with nothing read yet. */
  groupsEmpty?: number;
  yearsSigned: number[];
  run: Run | null;
  /** What has to be done before a load runs, in words (an e-transfer's bank account); the load refuses until then. */
  toDo?: string[];
  /** The units parked after repeated failures, oldest first: each is retryUnit's input (books, after the freeze). */
  parkedUnits?: ParkedUnit[];
  /** While mirroring ServiceTitan (BL-090): how far behind each list is, what's unfinished, the back-off. */
  mirrorHealth?: MirrorHealth | null;
}

/** migration.status's mirrorHealth (packages/modules migration/mirror-health.ts). */
export interface MirrorHealth {
  lastPass: {
    finishedAt: string;
    changed: number | null;
    incomplete: string[];
    rateLimited: boolean;
    error: string | null;
  } | null;
  lastCleanAt: string | null;
  lagMinutes: number | null;
  behindSince: string | null;
  incomplete: { entity: string; error: string | null }[];
  backoff: { intervalMinutes: number; nextAt: string | null } | null;
  photoListsWaiting: number;
  alertMinutes: number;
  /** The minutes between passes now (migration.mirrorEveryMinutes, or the back-off's when longer). */
  intervalMinutes: number;
  /** A list is behind past this: a pass that overdue (intervalMinutes + alertMinutes). */
  overdueMinutes: number;
  lagging: boolean;
  entities: {
    entity: string;
    kind: 'feed' | 'window' | 'reference' | 'photos' | 'load';
    lastGoodAt: string | null;
    lagMinutes: number;
    rows: number;
    incomplete: boolean;
    error: string | null;
    /** Why its last delete reconcile stopped, or null. */
    reconcileError: string | null;
  }[];
}

/** A list's name in words: "gross_pay_items" is "Gross pay items". */
export const listWords = (entity: string) => {
  const w = entity.replace(/_/g, ' ');
  return w.charAt(0).toUpperCase() + w.slice(1);
};

/** Minutes behind in words: "3 min behind", "2 h behind", "up to date" under a minute. */
export function behindWords(minutes: number | null): string {
  if (minutes === null) return 'not read yet';
  if (minutes < 1) return 'up to date';
  if (minutes < 120) return `${minutes} min behind`;
  if (minutes < 48 * 60) return `${Math.floor(minutes / 60)} h behind`;
  return `${Math.floor(minutes / 1440)} days behind`;
}

/** A read on time in words: "read 12 min ago", "up to date" under a minute. */
function readAgoWords(minutes: number): string {
  if (minutes < 1) return 'up to date';
  if (minutes < 120) return `read ${minutes} min ago`;
  return `read ${Math.floor(minutes / 60)} h ago`;
}

/**
 * The Migration screen's mirror panel in words (st-mirror spec §1): the last pass, the lag, what's
 * unfinished and why, and ServiceTitan's back-off as the tick keeps it; the lists furthest behind first.
 * A list is behind only once a pass is overdue (h.overdueMinutes, the alert's line): between passes on
 * time each list's last read is up to an interval old, and that's said as when it was read.
 */
export function mirrorPanel(
  h: MirrorHealth,
  when: (iso: string) => string,
): {
  lastPass: string;
  lag: string;
  unfinished: string;
  backoff: string | null;
  alert: string | null;
  rows: { entity: string; label: string; behind: string; detail: string | null; warn: boolean }[];
} {
  const p = h.lastPass;
  const over = (m: number) => m > h.overdueMinutes;
  return {
    lastPass: !p
      ? 'No pass yet'
      : p.error
        ? `${when(p.finishedAt)}: failed`
        : p.changed
          ? `${when(p.finishedAt)}: ${p.changed} changed`
          : `${when(p.finishedAt)}: nothing new`,
    lag: h.lagMinutes === null || over(h.lagMinutes) ? behindWords(h.lagMinutes) : 'on time',
    unfinished: h.incomplete.length
      ? h.incomplete.map((x) => listWords(x.entity)).join(', ')
      : 'Nothing unfinished',
    backoff: h.backoff
      ? `ServiceTitan asked Flowology to wait: a pass every ${h.backoff.intervalMinutes} minutes for now${h.backoff.nextAt ? `, the next at ${when(h.backoff.nextAt)}` : ''}.`
      : null,
    alert: h.lagging
      ? `A pass is overdue: the copy is ${behindWords(h.lagMinutes)}, past the ${h.overdueMinutes} minutes it's let go (a pass every ${h.intervalMinutes}, and ${h.alertMinutes} more). The office has been told. It clears itself once the copy catches up.`
      : null,
    rows: [...h.entities]
      .sort((a, b) => Number(b.incomplete) - Number(a.incomplete) || b.lagMinutes - a.lagMinutes)
      .map((e) => ({
        entity: e.entity,
        label: e.kind === 'photos' ? 'Photos' : e.kind === 'load' ? 'Loading' : listWords(e.entity),
        behind: !e.lastGoodAt
          ? 'not read whole yet'
          : over(e.lagMinutes)
            ? behindWords(e.lagMinutes)
            : readAgoWords(e.lagMinutes),
        detail: e.incomplete
          ? (e.error ?? 'unfinished')
          : e.kind === 'photos'
            ? h.photoListsWaiting
              ? `${h.photoListsWaiting} photo lists waiting`
              : null
            : [
                `${e.rows.toLocaleString('en-CA')} in the last read`,
                e.reconcileError && `Deletes not checked: ${e.reconcileError}`,
              ]
                .filter(Boolean)
                .join('. '),
        warn: e.incomplete || over(e.lagMinutes),
      })),
  };
}

export interface ParkedUnit {
  entity: string;
  year: number;
  attempts: number;
  lastError: string | null;
  since: string;
}

/** "invoices, 2025" or "customers, the sample". */
export const unitLabel = (u: Pick<ParkedUnit, 'entity' | 'year'>) =>
  `${u.entity.replace(/_/g, ' ')}, ${u.year === 0 ? 'the sample' : u.year}`;

export interface Step {
  key: string;
  label: string;
  /** 'owner', 'operator' (owner or manager) or 'derived' (done when the data says so). */
  by: string;
  done: boolean;
  doneAt: string | null;
  evidence: string | null;
  detail: string | null;
}

/** The steps a person records with evidence (migration.setPrecondition); the rest are derived. */
export const RECORDED = [
  'scopes_confirmed',
  'storage_ready',
  'bak_requested',
  'notice_given',
  'parallel_run_done',
  'st_off',
] as const;

/** Whether this person records this step: an open, recordable step, the owner's only for the owner. */
export function mayRecord(s: Step, held: readonly string[]): boolean {
  if (s.done || !(RECORDED as readonly string[]).includes(s.key)) return false;
  return s.by === 'owner' ? holds(held, OWNER_PERMS) : holds(held, OPERATE_PERMS);
}

export const WHO: Record<string, string> = {
  owner: 'The owner',
  operator: 'The owner or a manager',
  derived: 'From the data',
};

export const RUN_STATE: Record<Run['state'], string> = {
  off: 'Off',
  running: 'Running',
  paused: 'Paused',
};

export interface Mapping {
  group: string;
  stId: string;
  stName: string;
  target: string | null;
  /** The record's (or payment method's) name it maps to. */
  targetName?: string | null;
  createNew: boolean;
  uses: number;
  /** The years whose records use it (0: records every load reads). */
  years?: number[];
  proposed: string;
  /** Why it stands as it does: "new, as in ServiceTitan", "matches existing Plumbing", … (#82) */
  why?: string;
  /** Approved by the owner as it stands. */
  approved?: boolean;
  note: string | null;
}

export interface Group {
  group: string;
  values: number;
  unmapped: number;
  /** Nothing waits for approval (true for an empty group too: see empty). */
  approved: boolean;
  approvedBy: string | null;
  /** No value read yet: nothing to map or approve. */
  empty?: boolean;
}

/**
 * A mapping group's state in words (QA-867): a group with nothing read yet has nothing to approve, and
 * is never Approved, which only a person's approval of its values makes it.
 */
export function groupState(g: Group): { text: string; tone: 'ok' | 'bad' | null } {
  if (g.empty ?? g.values === 0) return { text: 'Nothing to map yet', tone: null };
  if (g.approved) return { text: 'Approved', tone: 'ok' };
  if (g.unmapped) return { text: `${g.unmapped} not mapped`, tone: 'bad' };
  return { text: 'Waiting for the owner', tone: null };
}

/** The overview's mapping tile (QA-867): of the groups with values read, how many a person approved. */
export function groupsTile(s: Pick<Status, 'groups' | 'groupsApproved'>): { big: string; label: string } {
  if (s.groups === 0) return { big: 'None yet', label: 'mapping groups to approve: nothing read yet' };
  return { big: `${s.groupsApproved} of ${s.groups}`, label: 'mapping groups approved' };
}

export const GROUP_LABEL: Record<string, string> = {
  business_unit: 'Departments',
  job_type: 'Job types',
  tax_zone: 'Tax zones',
  payment_type: 'Payment types',
  membership_type: 'Membership plans',
  stock_location: 'Stock locations',
};

/** A payment type maps to one of these, not a record. */
export const PAYMENT_METHODS: { value: string; label: string }[] = [
  { value: 'card', label: 'Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'etransfer', label: 'E-transfer' },
  { value: 'other', label: 'Other' },
];

/** What a mapping's select holds: a target id, "create", or nothing yet. */
export const choiceOf = (m: Mapping) => (m.createNew ? 'create' : (m.target ?? ''));

/**
 * The groups "Create all as new" offers, 1:1 with ServiceTitan's names (Amir: keep every ServiceTitan
 * value, combine later), and what their values are called, one and many. Payment types and tax zones
 * map onto fixed values, so they have none.
 */
const GROUP_NOUN: Record<string, [string, string]> = {
  business_unit: ['department', 'departments'],
  job_type: ['job type', 'job types'],
  membership_type: ['membership plan', 'membership plans'],
  stock_location: ['stock location', 'stock locations'],
};

/**
 * The values "Create all as new" would set to create one at the load: those still open (no record
 * and not created), never one a person already set; none in a group it doesn't offer.
 */
export function toCreateAll(group: string, mappings: readonly Mapping[]): Mapping[] {
  if (!GROUP_NOUN[group]) return [];
  return mappings.filter((m) => m.group === group && choiceOf(m) === '');
}

/** The confirm, in words: "Create 39 job types in Flowology from ServiceTitan's names? …". */
export function createAllText(group: string, n: number): string {
  const [one, many] = GROUP_NOUN[group] ?? ['value', 'values'];
  return `Create ${n} ${n === 1 ? one : many} in Flowology from ServiceTitan’s names? You can still change any of them before approving.`;
}

// ── approving in bulk (#82): every value arrives recommended; the owner approves a year's, a kind's or one ──

/** The years values are used in, newest first (0, the records every load reads, isn't a year). */
export function mappingYears(mappings: readonly Mapping[]): number[] {
  return [...new Set(mappings.flatMap((m) => m.years ?? []))].filter((y) => y > 0).sort((a, b) => b - a);
}

/** The years a value is used in ("2019–2023"), or what one only the every-load records use says. */
export const usedInText = (years: readonly number[] | undefined) => {
  const ys = (years ?? []).filter((y) => y > 0);
  return ys.length ? yearsText(ys) : 'every load';
};

/**
 * What a bulk approval would approve: the values in its scope (a year's records', a kind, or both)
 * not approved as they stand. One still unmapped is never approved: the server refuses the lot.
 */
export function toApprove(mappings: readonly Mapping[], scope: { year?: number; group?: string }): Mapping[] {
  return mappings.filter(
    (m) =>
      !m.approved &&
      (scope.group === undefined || m.group === scope.group) &&
      (scope.year === undefined || (m.years ?? []).includes(scope.year)),
  );
}

/** The values a person must map before an approval in that scope can go (none, with recommendations). */
export const unmappedIn = (ms: readonly Mapping[]) => ms.filter((m) => choiceOf(m) === '');

/** Open values (not approved) by year, then kind: what the screen's year table counts. */
export function openByYear(
  mappings: readonly Mapping[],
): { year: number; byGroup: Record<string, number>; total: number }[] {
  return mappingYears(mappings).map((year) => {
    const open = toApprove(mappings, { year });
    const byGroup: Record<string, number> = {};
    for (const m of open) byGroup[m.group] = (byGroup[m.group] ?? 0) + 1;
    return { year, byGroup, total: open.length };
  });
}

/** The confirm, in words, with the count it would approve: "Approve 412 job types as they stand?" */
export function approveText(scope: { year?: number; group?: string }, n: number): string {
  const noun = scope.group ? (GROUP_LABEL[scope.group] ?? scope.group).toLowerCase() : 'values';
  const what = n === 1 ? noun.replace(/s$/, '') : noun;
  const where = scope.year ? ` ${scope.year}’s records use` : '';
  return `Approve ${n.toLocaleString('en-CA')} ${what}${where} as they stand? A value changed later needs approving again.`;
}

/** migration.mapValue's input for a choice. */
export function mapInput(m: Mapping, choice: string) {
  return choice === 'create'
    ? { group: m.group, stId: m.stId, target: null, createNew: true }
    : { group: m.group, stId: m.stId, target: choice || null, createNew: false };
}

export interface Link {
  fileId: string;
  fileName: string | null;
  capturedAt: string | null;
  stJobId: string | null;
  rule: string;
  confidence: number;
  candidates: { stJobId: string; why: string }[];
  status: string;
  reason: string | null;
  /** The equipment a photo is on, and its address as "street, city"; null on any other photo. */
  equipment: { stId: string; name: string; location: string | null } | null;
}

/** "Water heater · 12 Elm St, Dartmouth", or null when the photo is not on equipment. */
export const equipmentText = (e: Link['equipment']) =>
  e ? [e.name, e.location].filter(Boolean).join(' · ') : null;

export const LINK_TABS: { status: 'review' | 'auto' | 'linked' | 'unlinkable'; label: string }[] = [
  { status: 'review', label: 'Waiting' },
  { status: 'auto', label: 'Linked by rule' },
  { status: 'linked', label: 'Linked by a person' },
  { status: 'unlinkable', label: 'No job' },
];

/** "92%". */
export const pct = (n: number) => `${Math.round(n * 100)}%`;

/** acceptLinks's floor: the server refuses below 0.80. */
export const MIN_CONFIDENCE = 0.8;

/**
 * The waiting links "accept all at or above" would take, as migration.acceptLinks picks them: a
 * proposed job, confidence at the floor, and no other candidate (a job id on the file has none).
 */
export const toAccept = (links: readonly Link[], min: number) =>
  links.filter((l) => !!l.stJobId && l.confidence >= min && l.candidates.length <= 1);
export const acceptable = (links: readonly Link[], min: number) => toAccept(links, min).length;

export interface ReviewItem {
  id: string;
  kind: string;
  entity: string;
  stId: string;
  year: number;
  message: string;
  status: string;
  resolution: string | null;
  /** ServiceTitan's staged record; null for a file. */
  raw: Record<string, unknown> | null;
  /** The last correction a person gave (the fields laid over `raw`), or null. */
  corrected: Record<string, unknown> | null;
  /** A job whose two sales ServiceTitan deleted: both, and which stays (BL-139); null for the rest. */
  sales: SalePair | null;
}

/** One of a job's sales ServiceTitan deleted, as it loaded here and with ServiceTitan's own dates. */
export interface Sale {
  stId: string;
  title: string | null;
  madeOn: string | null;
  soldOn: string | null;
  changedOn: string | null;
  subtotalCents: number | null;
  lines: { description: string; quantity: string; totalCents: number }[];
  seller: string | null;
  sellerStId: string | null;
  keptHere: boolean;
}

/** A job's sales ServiceTitan deleted, side by side, and the office's answer (migration.reviewKeepSale). */
export interface SalePair {
  job: string;
  sales: Sale[];
  /** a sale's stId, 'both' or 'neither'; null while nobody has said */
  keep: string | null;
  answeredBy: string | null;
  answeredAt: string | null;
  changeable: boolean;
}

export const KEEP_BOTH = 'both';
export const KEEP_NEITHER = 'neither';

/** "Sale 1", "Sale 2": how the office tells a job's sales apart, in their order (oldest first). */
export const saleLabel = (pair: SalePair, stId: string) =>
  `Sale ${pair.sales.findIndex((s) => s.stId === stId) + 1}`;

/** A day as the company reads it: "5 Mar 2024", or "Not recorded". */
export const saleDay = (iso: string | null, timeZone: string) =>
  iso
    ? new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium' }).format(new Date(iso))
    : 'Not recorded';

/** Who sold it, in words: the person here, else ServiceTitan's id for them, else nobody recorded. */
export const sellerText = (s: Pick<Sale, 'seller' | 'sellerStId'>) =>
  s.seller ?? (s.sellerStId ? `ServiceTitan person ${s.sellerStId} (not on the team here)` : 'Not recorded');

/** "2 × Water heater flush": a line as the office reads it, a quantity of 1 left out. */
export const saleLineText = (l: { description: string; quantity: string }) =>
  Number(l.quantity) === 1 ? l.description : `${Number(l.quantity)} × ${l.description}`;

/** What the answer does, in a sentence; null while nobody has said. */
export function keepText(pair: SalePair): string | null {
  if (pair.keep === null) return null;
  if (pair.keep === KEEP_BOTH) return 'The job keeps both sales.';
  if (pair.keep === KEEP_NEITHER) return 'The job keeps neither sale: both come off it on the next load.';
  return `The job keeps ${saleLabel(pair, pair.keep).toLowerCase()}: the other comes off it on the next load.`;
}

/** The toast once an answer is saved: what changes and when. */
export function keptToast(pair: SalePair, keep: string, changed: boolean): string {
  const what =
    keep === KEEP_BOTH
      ? 'both sales stay'
      : keep === KEEP_NEITHER
        ? 'neither sale stays'
        : `${saleLabel(pair, keep).toLowerCase()} stays`;
  return changed
    ? `Saved: ${what} on job ${pair.job}. The import follows it on its next load.`
    : `Already so: ${what} on job ${pair.job}.`;
}

export const REVIEW_KIND: Record<string, string> = {
  parse_error: 'Didn’t parse',
  load_error: 'Didn’t load',
  unmapped: 'Uses an unmapped value',
};

/** One group of the review queue (migration.reviewGroups): a table's open items waiting for one reason. */
export interface ReviewGroup {
  source: 'servicetitan' | 'brook';
  entity: string;
  reason: string;
  kind: string;
  count: number;
  years: { year: number; count: number }[];
  sample: string;
}

/** One row of an opened group (migration.reviewGroupRows): what a person answers is on it. */
export interface GroupRow {
  key: string;
  ids: string[];
  stId: string;
  year: number;
  message: string;
  material: { id: string; name: string | null } | null;
  quantity: string | null;
  units: { brook: string; servicetitan: string | null } | null;
  calls: { id: string; at: string }[] | null;
  receipt: { totalCents: number; taxCents: number } | null;
  person: string | null;
  sales: SalePair | null;
}

/** What a person does for a group, beside leaving it out: the answer only they have. */
export type GroupAnswer = 'costs' | 'units' | 'call' | 'tax' | 'person' | 'sale';

interface GroupText {
  title: string;
  sentence: string;
  answer?: GroupAnswer;
  /** the group's one action, in words */
  act?: string;
}

/**
 * Each reason in words: what the group is, one sentence on what happens to it, and the answer a person
 * gives where only a person knows it. A reason without words here shows its rows' own message.
 */
export const REVIEW_REASON: Record<string, GroupText> = {
  no_cost: {
    title: 'Stock with no cost',
    sentence:
      'Neither Brook’s tools nor ServiceTitan have a cost for these materials. Give each a cost per unit, or leave them out.',
    answer: 'costs',
    act: 'Give costs',
  },
  unit_mismatch: {
    title: 'Counted in another unit',
    sentence:
      'Brook counts these in another unit than ServiceTitan’s item. Say how many of ServiceTitan’s units one of his is.',
    answer: 'units',
    act: 'Say how many of one make the other',
  },
  call_ambiguous: {
    title: 'Two calls it could be',
    sentence: 'Two of ServiceTitan’s calls came in within two minutes of it. Pick the one it was.',
    answer: 'call',
    act: 'Pick which call',
  },
  tax_rate: {
    title: 'Tax that matches no rate',
    sentence: 'The receipt’s tax is neither 5 % GST nor 12 % GST and PST. Say what it was.',
    answer: 'tax',
    act: 'Say what it was',
  },
  person_not_matched: {
    title: 'Someone the import can’t match',
    sentence:
      'Brook’s tools name someone who matches no one here. Say who they are, and every row of theirs loads.',
    answer: 'person',
    act: 'Who is this?',
  },
  tech_not_matched: {
    title: 'A tech the import can’t match',
    sentence:
      'No ServiceTitan technician and no one here with their email. Say who they are, and every row of theirs loads.',
    answer: 'person',
    act: 'Who is this?',
  },
  two_deleted_sales: {
    title: 'Two sales ServiceTitan deleted',
    sentence:
      'ServiceTitan deleted both sales on each of these jobs, and their totals differ. Say which one the job keeps, both, or neither. You can change it until go-live.',
    answer: 'sale',
    act: 'Say which sale stays',
  },
  material_waits: {
    title: 'Waiting for its material',
    sentence:
      'Their material isn’t matched to a ServiceTitan item yet. They load on the import’s next run once it is.',
  },
  material_unknown: {
    title: 'A material Brook’s catalog doesn’t have',
    sentence: 'They name a material that isn’t in Brook’s catalog, so nothing can load them. Leave them out.',
  },
  material_conflict: {
    title: 'A material that matches two items',
    sentence:
      'Its code and its supplier’s part name two different ServiceTitan items. Leave it out, or fix one in ServiceTitan.',
  },
  call_or_job_waits: {
    title: 'Waiting for its call or job',
    sentence:
      'The ServiceTitan call or job it belongs to isn’t mirrored yet. It loads once it is; one from before the mirrored history is left out by itself.',
  },
  campaign_waits: {
    title: 'Waiting for its campaign',
    sentence: 'The ad campaign it belongs to isn’t mirrored yet. It loads once it is.',
  },
  native_materials: {
    title: 'Parts only Brook’s tools have',
    sentence: 'Made as Flowology’s own parts. Check them, then leave the item out to close it.',
  },
  not_a_phone_number: {
    title: 'A number that isn’t one',
    sentence: 'Its words are kept as a note on the customer or address when its year loads again.',
  },
  gone: {
    title: 'Gone in ServiceTitan',
    sentence:
      'ServiceTitan no longer has these files. A pricebook picture is left out by itself on the next load; a job’s file is a person’s to leave out.',
  },
  download_failed: {
    title: 'Didn’t download',
    sentence: 'Tried again on the next pass of the files (Photos, Retry).',
  },
  download_deferred: {
    title: 'Too large for now',
    sentence: 'Downloaded on a later pass of the files.',
  },
  type_not_shown: {
    title: 'A file type Flowology can’t show',
    sentence: 'Kept in the archive. Leave them out of the office if nothing needs them there.',
  },
  unmapped: {
    title: 'Uses an unmapped value',
    sentence: 'Map its value in Mappings: they resolve themselves on the next load.',
  },
};

/** The table a group is on, in words: Brook's import's without its prefixes. */
export const tableWords = (entity: string) =>
  entity
    .replace(/^brook_crm\./, '')
    .replace(/^crm_/, '')
    .replace(/_/g, ' ');

/** A group's title, its sentence and its answer: the reason's words, else its kind's and a row's own words. */
export function groupText(g: Pick<ReviewGroup, 'reason' | 'kind' | 'sample'>): GroupText {
  const known = REVIEW_REASON[g.reason];
  if (known) return known;
  const words = g.reason.replace(/_/g, ' ');
  return {
    title: REVIEW_KIND[g.reason] ?? words.charAt(0).toUpperCase() + words.slice(1),
    sentence: g.sample,
  };
}

/** "15 in 2026, 8 in 2024", newest first; nothing for Brook's import and reference data (no year). */
export const groupYears = (years: readonly { year: number; count: number }[]) =>
  years
    .filter((y) => y.year > 0)
    .map((y) => `${y.count.toLocaleString('en-CA')} in ${y.year}`)
    .join(', ');

/** A cost as a person types it: dollars, up to four decimals, more than zero. */
export const costOk = (text: string) => /^\d{1,7}(\.\d{1,4})?$/.test(text.trim()) && Number(text) > 0;

/** How many of one unit make another, as a person types it: a decimal more than zero. */
export const factorOk = (text: string) => /^\d{1,9}(\.\d{1,6})?$/.test(text.trim()) && Number(text) > 0;

/** One year's sign-off as the Overview says it (migration.status signOff). */
export interface SignOffYear {
  year: number;
  signed: boolean;
  reconciledAt: string | null;
  red: { name: string; note: string | null }[];
  stale: boolean;
}

/**
 * What's left for the owner's OK, a line a year, newest first: signed; not reconciled yet; what's red,
 * in words; loaded into since, so reconciled again first; or nothing left, so it can be signed.
 */
export function signOffLines(
  years: readonly SignOffYear[],
): { year: number; text: string; ready: boolean }[] {
  return years.map((y) => {
    const line = (text: string, ready = false) => ({ year: y.year, text, ready });
    if (y.signed) return line(`${y.year}: signed off`);
    if (!y.reconciledAt) return line(`${y.year}: not reconciled yet`);
    if (y.red.length)
      return line(
        `${y.year}, still red. ${y.red.map((r) => (r.note ? `${r.name}: ${r.note}` : r.name)).join('; ')}`,
      );
    if (y.stale) return line(`${y.year}: loaded into since it was reconciled: reconcile it again`);
    return line(`Nothing left: you can sign off ${y.year}`, true);
  });
}

/** How the pipeline tied a photo to a job, in words. */
export const RULE_LABEL: Record<string, string> = {
  st_job_id: 'The job id on the file',
  invoice_number: 'The invoice number on the file',
  time_gps: 'Taken at the job’s time and place',
  time_only: 'Taken during a job’s visit',
  location_latest: 'The latest job at the equipment’s address',
  none: 'No match',
};

/** The dialog starts from the record as the next load would read it: the raw one with the last correction over it. */
export const correctionStart = (it: Pick<ReviewItem, 'raw' | 'corrected'>) =>
  JSON.stringify({ ...it.raw, ...it.corrected }, null, 2);

/**
 * What a person changed against ServiceTitan's record, field by field: those are all that's sent
 * (the server lays them over the raw record, so a field left as it was stays as ServiceTitan sent it).
 * A field taken out can't be taken out that way, so it's named instead.
 */
export function correctionOf(
  raw: Record<string, unknown>,
  edited: Record<string, unknown>,
): { fields: Record<string, unknown>; removed: string[] } {
  const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
  const fields = Object.fromEntries(
    Object.entries(edited).filter(([k, v]) => !(k in raw) || !same(raw[k], v)),
  );
  return { fields, removed: Object.keys(raw).filter((k) => !(k in edited)) };
}

/** 'total: "12,O0" → "411.23"', one per changed field. */
export const changeLines = (raw: Record<string, unknown>, fields: Record<string, unknown>) =>
  Object.entries(fields).map(([k, v]) =>
    k in raw ? `${k}: ${JSON.stringify(raw[k])} → ${JSON.stringify(v)}` : `${k}: added, ${JSON.stringify(v)}`,
  );

/** "corrected" needs a JSON object of the fields; null when it isn't one. */
export function correctedOf(text: string): Record<string, unknown> | null {
  try {
    const v: unknown = JSON.parse(text);
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export interface Check {
  key: string;
  name: string;
  ok: boolean;
  expected: number | null;
  actual: number | null;
  note: string | null;
}

export interface Report {
  id: string;
  year: number;
  allGreen: boolean;
  computedAt: string;
  checks: Check[];
  signedOff: { by: string; typedName: string; at: string; binding: boolean } | null;
}

/** The years to reconcile: the sample (0) first, then each year the run pulls, newest first. */
export const yearsOf = (run: Run | null) => [0, ...[...(run?.years ?? [])].sort((a, b) => b - a)];

export const yearLabel = (y: number) => (y === 0 ? 'The sample' : String(y));

/** What still stops a year's sign-off, from its report; empty when it may be signed. */
export function signOffBlocks(r: Report | null): string[] {
  if (!r) return ['It hasn’t been reconciled yet.'];
  if (r.signedOff) return [];
  return r.checks.filter((c) => !c.ok).map((c) => `${c.name} doesn’t match.`);
}

/** The owner's written authorisation, in the company's own name (the prototype's letter). */
export function authorisationText(company: string): string {
  return [
    `I authorise Flowology to read ${company}’s own data from ServiceTitan through ${company}’s own API integration and credentials: customers, jobs, estimates, invoices, payments, memberships, timesheets, equipment, inventory, forms and their files, and call records.`,
    'Call recordings and voicemails from the last 2 years only, kept for 2 years and played by the owner and managers only.',
    'Everything is stored privately (encrypted, no public links) and pulled in yearly chunks, newest first, at a gentle off-hours pace.',
    `It finishes before ${company} gives ServiceTitan notice. API only, no browser automation.`,
  ].join('\n');
}

/** "2026", "2025 and 2026", "2021–2026" (a run of three or more), "2019, 2021 and 2026". */
export function yearsText(years: readonly number[]): string {
  const ys = [...new Set(years)].sort((a, b) => a - b);
  if (ys.length >= 3 && ys[ys.length - 1]! - ys[0]! === ys.length - 1) return `${ys[0]}–${ys[ys.length - 1]}`;
  if (ys.length <= 2) return ys.join(' and ');
  return `${ys.slice(0, -1).join(', ')} and ${ys[ys.length - 1]}`;
}

/**
 * Fewer records than this staged in a year, and the office's line doesn't call it ready to load
 * (history fix 2): ServiceTitan files the odd stray record under a year it has nothing else in (one
 * job and its visit in 2018), which isn't a year of history to bring over. The place still lists it.
 */
export const FEW_TO_LOAD = 10;

/** The years the line names as waiting: not loaded, and holding at least FEW_TO_LOAD staged records. */
export function yearsToName(status: Pick<Status, 'run' | 'staged'>): number[] {
  const run = status.run;
  if (!run) return [];
  const loaded = new Set(run.loadedYears ?? []);
  // an older server says no counts: every year it pulls, as before
  const rows = status.staged ? new Map(status.staged.map((y) => [y.year, y.rows])) : null;
  return run.years.filter((y) => !loaded.has(y) && (!rows || (rows.get(y) ?? 0) >= FEW_TO_LOAD));
}

/** Said on the window when the run changes here (start, pause, off, load), so the line reads it again (QA-876). */
export const MIGRATION_CHANGED = 'flow:migration-changed';

/** Who sees the office app's migration line (BL-103): the owner and the managers, who run it. */
export const BANNER_PERMS = OPERATE_PERMS;

/**
 * The office app's one line on the move while it's under way (BL-103): from the owner's written OK
 * until the company goes live after cutover; null before the OK and once live. Where the run is, then
 * what waits on a person (mappings to approve, records to review).
 */
export function migrationLine(authorised: boolean, status: Status | null): string | null {
  if (!authorised || !status) return null;
  const run = status.run;
  if (run?.phase === 'live') return null;
  if (run?.goingLive) return 'Going live: the switch stopped part way. Run it again to finish';
  const waits = [
    status.groups > status.groupsApproved
      ? `${status.groups - status.groupsApproved} mapping ${status.groups - status.groupsApproved === 1 ? 'group' : 'groups'} to approve`
      : null,
    status.reviewOpen > 0 ? `${status.reviewOpen.toLocaleString('en-CA')} to review` : null,
  ].filter((w): w is string => !!w);
  const tail = waits.length ? ` · ${waits.join(' · ')}` : '';
  if (!run) return `Bringing over ServiceTitan: authorised, not started yet${tail}`;
  // Turned off part way, nothing runs: the line says so, never that it's still at it (QA-876). At
  // rest (staged, loaded) off is how a run waits for the load, so it isn't said there
  const off = run.state === 'off';
  if (run.phase === 'mirror')
    return `Mirroring ServiceTitan${off ? ': turned off' : run.state === 'paused' ? ' (paused)' : ''}${tail}`;
  const loaded = run.loadedYears ?? [];
  const waiting = yearsToName(status);
  const is = (ys: readonly number[]) => (ys.length === 1 ? 'is' : 'are');
  const where =
    run.phase === 'inventory'
      ? 'counting what’s there'
      : run.phase === 'extract'
        ? `pulling ${run.years.length ? yearsText(run.years) : 'the sample'}${loaded.length ? `; ${yearsText(loaded)} loaded` : ''}`
        : run.phase === 'staged'
          ? waiting.length
            ? `${yearsText(waiting)} ${is(waiting)} ready to load`
            : 'ready to load'
          : run.phase === 'loading'
            ? `loading ${yearsText(run.loadYears?.length ? run.loadYears : run.years)}`
            : run.phase === 'loaded'
              ? waiting.length && loaded.length
                ? `${yearsText(loaded)} loaded; ${yearsText(waiting)} ${is(waiting)} ready to load`
                : loaded.length
                  ? `${yearsText(loaded)} loaded`
                  : 'loaded'
              : run.phase.replace(/_/g, ' ');
  const working = run.phase === 'inventory' || run.phase === 'extract' || run.phase === 'loading';
  if (off && working) return `Bringing over ServiceTitan: turned off while ${where}${tail}`;
  return `Bringing over ServiceTitan: ${where}${run.state === 'paused' ? ' (paused)' : ''}${tail}`;
}
